
import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { ComponentLoading } from "@/components/ui/loading";
import { useAuth } from "@/hooks/useAuthState";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Trophy,
  User,
  CheckCircle,
  ChevronRight
} from "lucide-react";
import type { UpdateUserProfile } from "@shared/schema";

// Use the same validation schema as the working profile system
const profileCompletionSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  phone: z.string().min(1, "Phone number is required").refine((phone) => {
    // Remove all spaces, dashes, keep only digits and +
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '').replace(/[^\d+]/g, '');
    const digitsOnly = cleanPhone.replace(/\+/g, '');
    
    // Check length: 10-12 digits
    if (digitsOnly.length < 10 || digitsOnly.length > 12) {
      return false;
    }

    // SA mobile prefixes (after removing country code)
    const validMobilePrefixes = ['060', '061', '062', '063', '064', '065', '066', '067', '068', '069', 
                                  '070', '071', '072', '073', '074', '075', '076', '077', '078', '079',
                                  '080', '081', '082', '083', '084', '085', '086', '087', '088', '089'];

    // Local format: 0XXXXXXXXX (10 digits starting with 06, 07, or 08)
    if (cleanPhone.match(/^0[6-8]\d{8}$/)) {
      const prefix = cleanPhone.substring(0, 3);
      return validMobilePrefixes.includes(prefix);
    }

    // International format: +27XXXXXXXXX or 27XXXXXXXXX (11-12 digits)
    if (cleanPhone.match(/^(\+27|27)[6-8]\d{8}$/)) {
      const localPart = cleanPhone.replace(/^(\+27|27)/, '0');
      const prefix = localPart.substring(0, 3);
      return validMobilePrefixes.includes(prefix);
    }

    return false;
  }, "Please enter a valid SA mobile number (10-12 digits, must start with 06, 07, or 08)"),
  dateOfBirth: z.string().min(1, "Date of birth is required").refine((date) => {
    const parsedDate = new Date(date);
    const today = new Date();
    const age = today.getFullYear() - parsedDate.getFullYear();
    return age >= 18 && age <= 120;
  }, "You must be at least 18 years old to use Prize Drop"),
  gender: z.string().min(1, "Please select your gender"),
  location: z.string().optional(),
  interests: z.string().optional()
});

type ProfileCompletionForm = z.infer<typeof profileCompletionSchema>;

export default function ProgressiveProfiling() {
  const [isCompleting, setIsCompleting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user, loading } = useAuth();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      console.log('[PROGRESSIVE PROFILING] Not authenticated, redirecting to auth');
      window.location.href = '/auth';
      return;
    }
  }, [user, loading]);

  // Initialize form with user data if available
  const form = useForm<ProfileCompletionForm>({
    resolver: zodResolver(profileCompletionSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      dateOfBirth: "",
      gender: "",
      location: "",
      interests: ""
    }
  });

  // Update form defaults when user data loads
  useEffect(() => {
    if (user) {
      form.reset({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: (user as any)?.phone || "",
        dateOfBirth: (user as any)?.dateOfBirth || "",
        gender: (user as any)?.gender || "",
        location: (user as any)?.location || "",
        interests: (user as any)?.interests || ""
      });
    }
  }, [user, form]);

  // Use the same profile update mutation as the working profile system
  const completeProfileMutation = useMutation({
    mutationFn: async (data: ProfileCompletionForm) => {
      try {
        console.log('[PROGRESSIVE PROFILING] Submitting profile data using standard profile API');

        // Use the same UpdateUserProfile structure as the working profile system
        const updateData: Partial<UpdateUserProfile> = {
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          dateOfBirth: data.dateOfBirth,
          gender: data.gender,
          location: data.location,
          interests: data.interests,
          marketingConsent: true // Default to true for progressive profiling users
        };

        console.log('[PROGRESSIVE PROFILING] Submitting data:', {
          ...updateData,
          phone: '***-***-****', // Don't log sensitive data
          email: '***@***.***'
        });

        // Use the same API endpoint as the working profile system
        const response = await apiRequest('PUT', '/api/user/profile', { body: updateData });
        console.log('[PROGRESSIVE PROFILING] Server response received successfully');
        return response;
      } catch (error) {
        console.error('[PROGRESSIVE PROFILING] Error in mutation function:', error);
        throw error;
      }
    },
    onSuccess: (result: any) => {
      console.log('[PROGRESSIVE PROFILING] Profile update successful:', result);
      toast({
        title: "Profile Completed! 🎉",
        description: "Your profile has been successfully updated. You can now participate in competitions!"
      });

      // Invalidate auth cache to refresh user data (same as working profile system)
      queryClient.invalidateQueries({ queryKey: ['/api/user/profile'] });
      queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user/dashboard-optimized'] });

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        window.location.href = '/user';
      }, 2000);
    },
    onError: (error: any) => {
      console.error('[PROGRESSIVE PROFILING] Profile update failed:', error);

      // Use the same error handling as the working profile system
      let errorMessage = "Failed to update profile. Please try again.";

      if (error?.message) {
        if (error.message.includes('validation')) {
          errorMessage = "Please check your information and try again. Make sure all fields are filled correctly.";
        } else if (error.message.includes('duplicate') || error.message.includes('unique')) {
          errorMessage = "This email is already taken. Please use a different one.";
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage = "Network error. Please check your connection and try again.";
        } else {
          errorMessage = error.message;
        }
      }

      toast({
        title: "Update Failed",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });

  const onSubmit = async (data: ProfileCompletionForm) => {
    setIsCompleting(true);
    try {
      // Check if phone is available before submitting
      if (data.phone) {
        const phoneCheckResponse = await fetch('/api/check-phone', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: data.phone })
        });
        const phoneCheckData = await phoneCheckResponse.json();
        
        if (!phoneCheckData.available) {
          form.setError('phone', {
            type: 'manual',
            message: phoneCheckData.message || 'This phone number is already registered and verified with another account.'
          });
          toast({
            title: "Phone Number Already In Use",
            description: phoneCheckData.message || 'This phone number is already registered and verified with another account.',
            variant: "destructive"
          });
          setIsCompleting(false);
          return; // Stop submission
        }
      }
      
      await completeProfileMutation.mutateAsync(data);
    } catch (error) {
      console.error('[PROGRESSIVE PROFILING] Form submission error:', error);
    } finally {
      setIsCompleting(false);
    }
  };

  // Calculate current completion percentage for progress display
  const getCurrentCompletion = () => {
    const values = form.getValues();
    // Required fields only for completion calculation
    const requiredFields = ['firstName', 'lastName', 'phone', 'dateOfBirth', 'gender'];
    const optionalFields = ['location', 'interests'];
    
    const completedRequired = requiredFields.filter(field => values[field as keyof ProfileCompletionForm]?.trim());
    const completedOptional = optionalFields.filter(field => values[field as keyof ProfileCompletionForm]?.trim());
    
    // Base completion from required fields (80%) + bonus from optional fields (20%)
    const requiredCompletion = (completedRequired.length / requiredFields.length) * 80;
    const optionalCompletion = (completedOptional.length / optionalFields.length) * 20;
    
    return Math.round(requiredCompletion + optionalCompletion);
  };

  // Calculate completion based on actual user data
  const getActualCompletion = () => {
    if (!user) return 0;
    
    const requiredFields = ['firstName', 'lastName', 'phone', 'dateOfBirth', 'gender'];
    const optionalFields = ['location', 'interests'];
    
    const completedRequired = requiredFields.filter(field => {
      const value = (user as any)[field];
      return value && value.toString().trim();
    });
    
    const completedOptional = optionalFields.filter(field => {
      const value = (user as any)[field];
      return value && value.toString().trim();
    });
    
    // Base completion from required fields (80%) + bonus from optional fields (20%)
    const requiredCompletion = (completedRequired.length / requiredFields.length) * 80;
    const optionalCompletion = (completedOptional.length / optionalFields.length) * 20;
    
    return Math.round(requiredCompletion + optionalCompletion);
  };

  const [currentCompletion, setCurrentCompletion] = useState(0);

  // Initialize completion with actual user data
  useEffect(() => {
    if (user) {
      const actualCompletion = getActualCompletion();
      console.log('[PROGRESSIVE PROFILING] Actual completion calculated:', actualCompletion);
      console.log('[PROGRESSIVE PROFILING] User data:', {
        firstName: user.firstName,
        lastName: user.lastName,
        phone: (user as any).phone,
        dateOfBirth: (user as any).dateOfBirth,
        gender: (user as any).gender,
        location: (user as any).location,
        interests: (user as any).interests
      });
      
      // Phone is now REQUIRED - don't redirect if missing
      if (!user.phone) {
        console.log('[PROGRESSIVE PROFILING] Phone number is required - staying on page');
        return;
      }

      // If profile is at least 80% complete AND has phone, redirect to dashboard
      if (actualCompletion >= 80) {
        console.log('[PROGRESSIVE PROFILING] Profile is sufficiently complete (80%+), redirecting to dashboard');
        toast({
          title: "Welcome back! 🎉",
          description: "Your profile is ready. Redirecting to your dashboard..."
        });
        setTimeout(() => {
          window.location.href = '/user';
        }, 1000);
        return;
      }
      
      setCurrentCompletion(actualCompletion);
    }
  }, [user, toast]);

  // Update completion percentage as user types
  useEffect(() => {
    const subscription = form.watch(() => {
      setCurrentCompletion(getCurrentCompletion());
    });
    return () => subscription.unsubscribe();
  }, [form]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <div className="max-w-4xl mx-auto">
          <ComponentLoading text="Loading your profile..." className="py-12" />
        </div>
      </div>
    );
  }

  // Don't render anything if not authenticated (will redirect)
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="max-w-2xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Complete Your Profile
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Help us personalize your experience and earn bonus entries
          </p>
        </div>

        {/* Progress Overview */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Profile Progress</span>
              <span className="text-2xl font-bold text-blue-600">{currentCompletion}%</span>
            </CardTitle>
            <CardDescription>
              {currentCompletion}% complete • Required fields: {Math.max(0, 5 - Math.floor((currentCompletion * 5) / 80))} remaining
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Progress value={currentCompletion} className="h-3" />
          </CardContent>
        </Card>

        {/* Profile Completion Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Complete Your Profile
            </CardTitle>
            <CardDescription>
              Please fill in all fields to complete your profile setup
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* First and Last Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="John"
                            {...field}
                            data-testid="input-firstName"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Doe"
                            {...field}
                            data-testid="input-lastName"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Phone Number */}
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Number</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="27712345678 or 0712345678"
                          {...field}
                          onBlur={async (e) => {
                            field.onBlur();
                            const phone = e.target.value.trim();
                            if (phone) {
                              try {
                                const response = await fetch('/api/check-phone', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ phone })
                                });
                                const data = await response.json();
                                if (!data.available) {
                                  form.setError('phone', {
                                    type: 'manual',
                                    message: data.message || 'This phone number is already registered.'
                                  });
                                }
                              } catch (error) {
                                console.error('[PHONE CHECK] Error checking phone availability:', error);
                              }
                            }
                          }}
                          data-testid="input-phone"
                        />
                      </FormControl>
                      <FormDescription>
                        SA mobile: 10-12 digits, must start with 06, 07, or 08
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Date of Birth */}
                <FormField
                  control={form.control}
                  name="dateOfBirth"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date of Birth</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          data-testid="input-dateOfBirth"
                        />
                      </FormControl>
                      <FormDescription>
                        You must be 18 or older to use Prize Drop
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Gender */}
                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gender</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-gender">
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                          <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Location */}
                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Location (Optional)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Cape Town, Western Cape"
                          {...field}
                          data-testid="input-location"
                        />
                      </FormControl>
                      <FormDescription>
                        City and province in South Africa (optional)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Interests */}
                <FormField
                  control={form.control}
                  name="interests"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Interests (Optional)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Tell us about your hobbies and interests... (optional)"
                          {...field}
                          data-testid="input-interests"
                          rows={3}
                        />
                      </FormControl>
                      <FormDescription>
                        Help us personalize your experience (optional)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Submit Button */}
                <div className="pt-6">
                  <Button
                    type="submit"
                    disabled={isCompleting || completeProfileMutation.isPending}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 text-lg"
                    data-testid="button-complete-profile"
                  >
                    {isCompleting || completeProfileMutation.isPending ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Completing Profile...
                      </div>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Complete Profile
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Benefits Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Benefits of Completing Your Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-gray-600 dark:text-gray-400">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Enter daily, weekly, and monthly competitions
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Earn bonus entries through social sharing
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Personalized competition recommendations
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Access to exclusive challenges and prizes
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
